//core
import React, {useEffect, useRef, useState} from "react";
import { useTranslation } from "react-i18next";
import { useField, useFormikContext } from "formik";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { isValid } from "date-fns";
import {DateTimePicker, DesktopDateTimePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {FormControl, FormHelperText, InputAdornment, IconButton, Box} from "@mui/material";

//locales
import ruLocale from "date-fns/locale/ru";
import ukLocale from "date-fns/locale/uk";
import enLocale from "date-fns/locale/en-US";
import esLocale from "date-fns/locale/es";
import trLocale from "date-fns/locale/tr";
import koLocale from "date-fns/locale/ko";
import elLocale from "date-fns/locale/el";
import frLocale from "date-fns/locale/fr";
import ptLocale from "date-fns/locale/pt";
import thLocale from "date-fns/locale/th";
import viLocale from "date-fns/locale/vi";
import idLocale from "date-fns/locale/id";

//styles
import styles from "./styles/styles.module.scss";
import Calendar from "@mui/icons-material/Event";
import { DateOrTimeView } from "@mui/x-date-pickers/models";


//types
interface IMuiFieldProps {
  id?: string;
  name: string;
  autoComplete?: string;
  label: string;
  value?: any;
  secondValue?: any;
  maxDate?: any,
  minDate?: any,
  //type?: React.InputHTMLAttributes<unknown>['type'];
  hideError?: boolean,
  placeholder?: string,
  disabled?: boolean,
  className?: string,
  helperText?: string,
  dataId?: string,
  InputProps?: any,
  error?: boolean | string,
  validation?: (value: any) => any,
  validate?: (value: any) => any,
  onChange?: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>;
  inputFormat?: string;
  views?: DateOrTimeView[];
  onCloseCallback?: () => void;
  closeOnOutside?: boolean;
}

const localeMap = {
  en: enLocale,
  ru: ruLocale,
  uk: ukLocale,
  es: esLocale,
  tr: trLocale,
  ko: koLocale,
  el: elLocale,
  fr: frLocale,
  pt: ptLocale,
	th: thLocale,
	vi: viLocale,
	id: idLocale,
};

export const MuiSingleDateTimePicker = (props: IMuiFieldProps) => {
  const [field, meta] = useField(props);
  const ref: any = useRef<HTMLDivElement>();

  const { t, i18n } = useTranslation();

  const { setFieldValue, setFieldError } = useFormikContext();

  const {
    id,
    name,
    autoComplete,
    label,
    value,
    secondValue,
    disabled = false,
    maxDate,
    minDate,
    error = '',
    hideError = false,
    //views = [],
    inputFormat = 'dd/MM/yyyy HH:mm',
    views = ["year", "month", "day", "hours", "minutes"],
    onCloseCallback,
    closeOnOutside = false
  } = props;


  const [fieldVal, setFieldVal] = React.useState<Date | null>(new Date(value));
  const [open, setOpen] = useState(false);
  const [locale, setLocale] = useState<object>(localeMap.en);
  const [hasValidationError, setHasValidationError] = useState<boolean>(false);

  // Validate date function
  const validateDate = (date: Date | null): boolean => {
    if (!date) return true; // null is considered valid (empty field)

    // Check if date is valid
    if (!isValid(date)) {
      return false;
    }

    // Check against min/max dates if provided
    if (minDate && date < new Date(minDate)) {
      return false;
    }

    if (maxDate && date > new Date(maxDate)) {
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (!value || value === '' || value === null || value === undefined) {
      setFieldVal(null);
      return;
    }
    const date = new Date(value);
    setFieldVal(isValid(date) ? date : null);
  }, [value]);

  useEffect(() => {
    if (i18n.language === 'ru') {
      setLocale(localeMap.ru);
    }
    if (i18n.language === 'en') {
      setLocale(localeMap.en);
    }
    if (i18n.language === 'uk') {
      setLocale(localeMap.uk);
    }
    if (i18n.language === 'es') {
      setLocale(localeMap.es);
    }
    if (i18n.language === 'tr') {
      setLocale(localeMap.tr);
    }
    if (i18n.language === 'ko') {
      setLocale(localeMap.ko);
    }
    if (i18n.language === 'el') {
      setLocale(localeMap.el);
    }
    if (i18n.language === 'fr') {
      setLocale(localeMap.fr);
    }
    if (i18n.language === 'pt') {
      setLocale(localeMap.pt);
    }
	  if (i18n.language === 'th') {
		  setLocale(localeMap.th);
	  }
	  if (i18n.language === 'vi') {
		  setLocale(localeMap.vi);
	  }
	  if (i18n.language === 'id') {
		  setLocale(localeMap.id);
	  }
  }, [i18n.language]);

  useEffect(() => {
    if (fieldVal !== null) {
      if (name === 'at_end_show') {
        if (new Date(fieldVal) < new Date(secondValue)) {
          setFieldValue(field.name, secondValue);
          setFieldVal(secondValue);
        }
      }
      if (name === 'at_beginning_show') {
        if (new Date(fieldVal) > new Date(secondValue)) {
          setFieldValue('at_end_show', fieldVal);
          setFieldVal(fieldVal);
        }
      }
    }

  }, [fieldVal, minDate]);

  const handleClose = () => {
    setOpen(false);
    if (typeof onCloseCallback === 'function') {
      onCloseCallback();
    }
  };

  const handleDateChange = (newValue: Date | null) => {
    setFieldVal(newValue);

    const isValidDate = validateDate(newValue);
    setHasValidationError(!isValidDate);

    if (isValidDate) {
      // Clear any existing error and set the valid value
      setFieldError(name, undefined);
      setFieldValue(field.name, newValue, true);
    } else {
      // Set error when date is not valid
      let errorMessage = 'Invalid date';

      if (newValue && !isValid(newValue)) {
        errorMessage = 'Invalid date format';
      } else if (minDate && newValue && newValue < new Date(minDate)) {
        errorMessage = 'Date is before minimum allowed date';
      } else if (maxDate && newValue && newValue > new Date(maxDate)) {
        errorMessage = 'Date is after maximum allowed date';
      }

      setFieldError(name, errorMessage);
    }
  };

  return (
    <LocalizationProvider
      //@ts-ignore
      dateAdapter={AdapterDateFns} adapterLocale={locale}>
      <div className={styles.formField}>
        <FormControl fullWidth>
          <DateTimePicker
            views={views}
            ampm={false}
            open={open}
            onOpen={() => setOpen(true)}
            onClose={handleClose}
            label={label}
            disabled={disabled}
            //value={new Date(value)}
            value={fieldVal}
            format={i18n.language === 'en' ? 'MM/dd/yyyy HH:mm' : inputFormat}
            // @ts-ignore
            closeOnSelect={true}
            {...(maxDate ? {maxDate: new Date(maxDate)} : {})}
            {...(minDate ? {minDate: new Date(minDate)} : {})}
            // @ts-ignore
            onChange={handleDateChange}
            slotProps={{
              popper: {
                disablePortal: false,
                ref: ref,
                style: { zIndex: 999999 }
              },
              textField: {
                size: 'small',
                id: id,
                className: `${styles.input} ${ !!(meta.touched && !!meta.error && meta.error !== 'error') ? styles.inputError : styles.inputErrorDef }`,
                InputProps: {
                  endAdornment: <InputAdornment position="end">
                    <IconButton
                      disabled={disabled}
                      size="small"
                      className={styles.iconButton}
                      sx={{ padding: '2px' }}
                      onClick={() => {
                        if (disabled) {return false;}
                        setOpen(!open);
                      }}
                    >
                      <Calendar color={disabled ? 'disabled' : 'action'} />
                    </IconButton>
                  </InputAdornment>,
                },
                autoComplete: autoComplete,
                ...(hideError ? {error: false} : {}),
                ...( !!(meta.touched && meta.error) ? {color: 'error'} : {})
              },
              actionBar: ({ wrapperVariant }) => {
                return {
                  actions: wrapperVariant === 'desktop' ? ['accept'] : ['accept'],
                }
              },
            }}
          />
          { meta.touched && !!meta.error && meta.error !== 'error' ? (
            <FormHelperText error>{meta.error}</FormHelperText>
          ) : null }
        </FormControl>
      </div>
    </LocalizationProvider>
  );
};
